import { TextField, Autocomplete, AutocompleteChangeReason, CircularProgress } from "@mui/material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { PropsFilter } from "../../models/api/props-filter";
import { RefModel } from "../../models/base/ref-model";

const PAGE_SIZE = 5;

function MultipleServiceAutocompleteDropdown<T>({
  labelKey, // translation key for label
  field,
  fieldProp = "name",
  isMultiSelect,
  multipleServiceApi,
  propsFilter,
  apiLoadItemsPath,
  propsItem,
  selectedValue,
  handleChange,
  sxProps,
  inputVariant = "outlined",
  disabled,
  required,
  excludeIds,
  childField,
  childFieldProp = "name",
}: Props<T>) {
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    query: "",
    prevQuery: "",
    value: isMultiSelect ? [] : null,
    loading: false,
    error: "",
    open: false,
    items: [],
    totalItems: 0,
    pageNumber: 0,
  });

  const totalItemsSet = useRef<number[]>([]);

  const loadDataWhenInit = useCallback(
    async (query = "", pageNumber = 0, signal: AbortSignal) => {
      const response = await Promise.all(
        multipleServiceApi.map((service) =>
          service.loadItems(
            {
              ids: [],
              notIds: excludeIds ?? [],
              createdByUsers: [],
              modifiedByUsers: [],
              isDeleted: false,
              ...(propsFilter ?? {}),
            } as PropsFilter,
            query,
            [...service.defaultSort],
            { pageNumber, pageSize: PAGE_SIZE },
            false,
            60,
            signal
          )
        )
      );

      totalItemsSet.current = response.map((el) => el.totalItems);
    },
    [multipleServiceApi, propsFilter, excludeIds]
  );

  const handleChangePage = (pageNumber: number): void => setState((state) => ({ ...state, pageNumber }));

  const handleChangeQuery = (query: string): void =>
    setState((state) => ({
      ...state,
      pageNumber: 0,
      prevQuery: state.query,
      query,
    }));

  const handleChangeValue = (value: RefModel & [], reason: AutocompleteChangeReason) =>
    setState((state) => ({
      ...state,
      value,
      ...(reason === "clear" ? { query: "", prevQuery: "" } : {}),
    }));

  const loadData = useCallback(
    async (query = "", pageNumber = 0, signal: AbortSignal) => {
      let pageSizeQuery = PAGE_SIZE;

      const response = await Promise.all(
        multipleServiceApi.map((service, idx) => {
          if (totalItemsSet.current[idx] === 0) {
            pageSizeQuery = 0;
          } else {
            totalItemsSet.current[idx] = totalItemsSet.current[idx] - pageSizeQuery;
          }

          return service.loadItems(
            {
              ids: [],
              notIds: excludeIds ?? [],
              createdByUsers: [],
              modifiedByUsers: [],
              isDeleted: false,
              ...(propsFilter ?? {}),
            } as PropsFilter,
            query,
            [...service.defaultSort],
            { pageNumber, pageSize: pageSizeQuery },
            false,
            60,
            signal
          );
        })
      );

      const totalItems = response.map((el) => el.totalItems).reduce((acc, curr) => acc + curr, 0);
      const error = response.map((el) => el.error).find((el) => el !== "") ?? "";

      const items = response.reduce((acc, curr, idx) => (idx === 0 ? acc : [...acc, ...curr.items]), response[0].items);

      return {
        totalItems,
        error,
        items,
      };
    },
    [multipleServiceApi, apiLoadItemsPath, propsFilter, excludeIds]
  );

  // loadData ---- ON INIT
  const loadDataOnInit = useCallback(
    async (query: string, signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        await loadDataWhenInit("", 0, signal);

        const { items, totalItems, error } = await loadData(query, 0, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items,
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      }
    },
    [loadData]
  );

  // loadData --- ON SCROLL
  const loadDataOnScroll = useCallback(
    async (pageNumber: number, signal: AbortSignal): Promise<void> => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(state.query, pageNumber, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items: [...state.items, ...items],
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      }
    },
    [loadData, state.query]
  );

  const setOpen = (value: boolean): void => setState((state) => ({ ...state, open: value }));

  // reset state on close AutocompleteDropdown
  const closeAndReset = () =>
    setState((state) => ({
      ...state,
      open: false,
      pageNumber: 0,
      items: [],
      totalItems: 0,
    }));

  useEffect(() => {
    if (selectedValue) {
      setState((state) => ({
        ...state,
        value: selectedValue,
        query: selectedValue[field] ?? "",
        prevQuery: selectedValue[field] ?? "",
      }));
    } else {
      setState((state) => ({
        ...state,
        value: null,
        query: "",
        prevQuery: "",
      }));
    }
  }, [selectedValue, field]);

  useEffect(() => {
    if (propsItem && propsItem[field]) {
      if (typeof propsItem[field] === "string") {
        setState((state) => ({
          ...state,
          value: propsItem,
          query: propsItem[field] ?? "",
          prevQuery: propsItem[field] ?? "",
        }));
      } else {
        setState((state) => ({
          ...state,
          value: propsItem[field],
          query: propsItem[field][fieldProp] ?? "",
          prevQuery: propsItem[field][fieldProp] ?? "",
        }));
      }
    }
  }, [propsItem, field, fieldProp]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.open) {
      // onInit
      if (state.pageNumber === 0 && state.query === state.prevQuery)
        loadDataOnInit(state.query, abortController.signal);

      // onSearch
      if (state.pageNumber === 0 && state.query !== state.prevQuery)
        timer = setTimeout(() => loadDataOnInit(state.query, abortController.signal), 500);

      // onScroll
      if (state.pageNumber > 0) loadDataOnScroll(state.pageNumber, abortController.signal);
    }

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [state.open, state.query, state.prevQuery, state.value, state.pageNumber, loadDataOnInit, loadDataOnScroll]);

  return (
    <Autocomplete
      aria-required={required}
      open={state.open}
      onOpen={() => !disabled && setOpen(true)}
      loading={state.loading}
      onClose={closeAndReset}
      sx={{ ...sxProps }}
      filterOptions={(options) => options}
      multiple={isMultiSelect}
      value={state.value}
      onChange={(_, value, reason) => {
        handleChange(field, value);
        handleChangeValue(value, reason);
      }}
      onInputChange={(_, value) => handleChangeQuery(value)}
      ListboxProps={{
        onScroll: ({ currentTarget: { scrollTop, clientHeight, scrollHeight } }: React.SyntheticEvent) => {
          const { pageNumber, totalItems } = state;
          const maxPages = Math.ceil(totalItems / PAGE_SIZE);

          if (scrollHeight - Math.ceil(scrollTop) <= clientHeight && pageNumber < maxPages - 1 && !state.loading)
            handleChangePage(pageNumber + 1);
        },
        style: { maxHeight: "150px", background: "white" },
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id || option.id === value || option.name === value}
      getOptionLabel={(option) => {
        if (option[fieldProp]) {
          if (!!childField && option[childField]) {
            return `${option[fieldProp]} (${option[childField][childFieldProp]})`;
          } else {
            return option[fieldProp];
          }
        }
        if (option.email) {
          return option.email;
        }
        return "";
      }}
      options={state.items}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={labelKey && `${t(`${labelKey}`)}`}
          variant={inputVariant}
          placeholder={t("components.autocomplete_dropdown.placeholders.please_select")}
          InputProps={{
            ...params.InputProps,
            disabled,
            endAdornment: (
              <>
                {state.loading && (
                  <CircularProgress
                    color="inherit"
                    size={20}
                  />
                )}
                {!disabled && params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default MultipleServiceAutocompleteDropdown;
